import styled from 'styled-components';
import { UseFormReturn } from 'react-hook-form';

import { ProjectInfoFormValues } from 'types';
import useProjectInfo from './hooks/useProjectInfo';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import Modal, { ModalHeader, ModalForm } from 'components/Modal';
import FormRow from 'components/FormRow';
import Select from 'components/ui/Select';
import Radio from 'components/ui/Radio';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const FrameworkView = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FrameworkButtonView = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DirectionView = styled.div`
  display: flex;
  width: 100%;
  gap: 20px;
`;

const Direction = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
`;

const DirectionImage = styled.div`
  width: 100%;
  border: 1px solid ${(props) => props.theme.global.borderColor};
  border-radius: 4px;
  height: 120px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

interface ProjectInfoModalProps {
  isUpdate?: boolean;
  isLoading: boolean;
  onSubmit: () => void;
  onPrevClick?: () => void;
  useFormReturn: UseFormReturn<ProjectInfoFormValues>;
}

const ProjectInfoModal = ({
  useFormReturn,
  isLoading,
  isUpdate = false,
  onSubmit,
  onPrevClick,
}: ProjectInfoModalProps) => {
  const {
    isDarkMode,
    templates,
    frameworkVersions,
    selectedFramework,
    formState: { errors },
    register,
    handleFrameworkClick,
  } = useProjectInfo({ useFormReturn });

  return (
    <Modal maxWidth={500}>
      <ModalHeader {...(onPrevClick && { onPrevClick })}>프로젝트 정보를 입력하세요</ModalHeader>
      <ModalForm isLoading={isLoading} buttonText={isUpdate ? '저장하기' : '프로젝트 만들기'} onSubmit={onSubmit}>
        <Container>
          <FormRow label="프로젝트 이름" error={errors.projectName?.message}>
            <Input {...register('projectName')} placeholder="2-50자 이내로 입력해주세요." />
          </FormRow>
          {!isUpdate && (
            <FormRow label="템플릿" error={errors.template?.message}>
              <Select {...register('template')}>
                {templates.map((template) => (
                  <option key={template.id} value={template.id} disabled={template.id !== 'free'}>
                    {template.title}
                  </option>
                ))}
              </Select>
            </FormRow>
          )}
          <FormRow label="프레임워크 / 버전" error={errors.framework?.message ?? errors.frameworkVersion?.message}>
            <FrameworkView>
              <FrameworkButtonView>
                <Button $width={100} onClick={handleFrameworkClick}>
                  PyTorch
                </Button>
                <Button $width={100} disabled onClick={handleFrameworkClick}>
                  TensorFlow
                </Button>
              </FrameworkButtonView>
              <Select {...register('frameworkVersion')} maxWidth={130} disabled={isUpdate}>
                {frameworkVersions.map((version) => (
                  <option key={`framework${selectedFramework}${version}`} value={version}>
                    {version}
                  </option>
                ))}
              </Select>
            </FrameworkView>
          </FormRow>
          <FormRow label="모델 방향" error={errors.direction?.message}>
            <DirectionView>
              <Direction>
                <Radio
                  {...register('direction')}
                  id="directionVertical"
                  label="수직"
                  value="vertical"
                  disabled={isUpdate}
                />
                <DirectionImage>
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/svgs/vertical${isDarkMode ? '_dark' : ''}.svg`}
                    alt="vertical model"
                  />
                </DirectionImage>
              </Direction>
              <Direction>
                <Radio
                  {...register('direction')}
                  id="directionHorizontal"
                  label="수평"
                  value="horizontal"
                  disabled={isUpdate}
                />
                <DirectionImage>
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/svgs/horizontal${isDarkMode ? '_dark' : ''}.svg`}
                    alt="horizontal model"
                  />
                </DirectionImage>
              </Direction>
            </DirectionView>
          </FormRow>
        </Container>
      </ModalForm>
    </Modal>
  );
};

export default ProjectInfoModal;
